"use client";

import React from "react";
import { Loader2 } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function RequestAQuote() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return;
}
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-12 bg-white/5 border border-white/10 rounded-none text-center animate-in fade-in zoom-in duration-500 max-w-2xl mx-auto">
        <h3 className="text-3xl font-sans font-black text-white mb-6 uppercase tracking-tight">Project Received</h3>
        <p className="text-white/80 mb-8 text-lg font-sans">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="bg-white text-black border-none hover:bg-white/90 transition-colors uppercase tracking-widest font-black rounded-none px-12 h-14">
          Submit New Vision
        </Button>
      </div>;
  }
  const inputStyles = "border-0 border-b border-muted-foreground/30 bg-transparent rounded-none px-0 py-4 h-auto text-white placeholder:text-muted-foreground/20 focus-visible:ring-0 focus-visible:border-white focus-visible:border-b-2 transition-all duration-300 shadow-none text-lg font-sans";
  const labelStyles = "text-muted-foreground uppercase tracking-widest text-[10px] font-mono mb-2";
  return <Form onSubmit={handleSubmit} className="space-y-12">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="ENTER NAME" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="text-destructive mt-2 font-mono text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="EMAIL@DOMAIN.COM" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="text-destructive mt-2 font-mono text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Contact number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Contact Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 (0) 000 000 000" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="text-destructive mt-2 font-mono text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="address" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Site Location</FormLabel>
              <FormControl>
                <Input placeholder="PROJECT ADDRESS" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="text-destructive mt-2 font-mono text-[10px]" />
            </FormItem>} />
      </div>

      <FormField control={control} name="project_details" rules={{
      required: "Project scope description required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelStyles}>Project Scope</FormLabel>
            <FormControl>
              <Textarea placeholder="DESCRIBE YOUR CONSTRUCTION VISION..." className={cn(inputStyles, "min-h-[100px] resize-none")} {...field} />
            </FormControl>
            <FormMessage className="text-destructive mt-2 font-mono text-[10px]" />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
        <FormField control={control} name="preferred_start_date" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Commencement Date</FormLabel>
              <FormControl>
                <Input type="date" className={cn(inputStyles, "block w-full [color-scheme:dark]")} {...field} />
              </FormControl>
              <FormMessage className="text-destructive mt-2 font-mono text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="budget" rules={{
        min: {
          value: 1000,
          message: "Minimum budget is £1,000"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelStyles}>Capital Allocation (GBP)</FormLabel>
              <FormControl>
                <Input type="number" placeholder="ESTIMATED BUDGET" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage className="text-destructive mt-2 font-mono text-[10px]" />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-none text-destructive text-xs font-mono text-center">
          {submitError}
        </div>}

      <div className="pt-8">
        <Button type="submit" disabled={isSubmitting} className="group relative w-full h-20 text-xl font-black tracking-[0.2em] uppercase bg-white text-black hover:text-white transition-colors duration-500 rounded-none overflow-hidden">
          <span className="relative z-10 flex items-center justify-center">
            {isSubmitting ? <>
                <Loader2 className="animate-spin mr-3 size-6" />
                PROCESSING...
              </> : "SUBMIT PROJECT"}
          </span>
          <div className="absolute inset-0 bg-black translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out z-0" />
        </Button>
      </div>
    </Form>;
}